import { Component, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { Subscription, Observable, debounceTime, distinctUntilChanged, filter, skip, tap } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { Event } from '../../models';
import { OrganizersService, EventsService } from '../../services';
import { EventsBrowseService } from '../events-browse.service';
import { TranslocoModule } from '@ngneat/transloco';
import { EventCardComponent } from '../../shared/components/event-card/event-card.component';
import { LoaderComponent } from '../../shared/components/loader/loader.component';
import { MatButton, MatAnchor } from '@angular/material/button';

@Component({
  selector: 'app-events-list',
  templateUrl: './events-list.component.html',
  styleUrls: ['./events-list.component.scss'],
  standalone: true,
  imports: [
    TranslocoModule,
    EventCardComponent,
    LoaderComponent,
    MatButton,
    MatAnchor,
    RouterLink,
  ],
})
export class EventsListComponent implements OnInit, OnDestroy {
  eventsBrowseService = inject(EventsBrowseService);
  private eventsService = inject(EventsService);
  private organizersService = inject(OrganizersService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  public oldWidget: boolean;
  public events: Event[];
  public isLoadingEvents = true;
  public isBrowser = false;

  public limit = 21;
  public offset = 0;
  public moreToLoad = true;
  private newEventsService: OrganizersService | EventsService;

  private eventsSubscription: Subscription;
  private formSubscription: Subscription;

  constructor() {
    const platformId = inject(PLATFORM_ID);

    this.oldWidget = this.router.url.startsWith('/w/');
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit() {
    this.events = [];
    this.isLoadingEvents = true;
    this.eventsBrowseService.browseForm.controls.sort?.enable({emitEvent: false, onlySelf: true});
    this.eventsBrowseService.browseForm.controls.displayStyle?.setValue('list', {emitEvent: false, onlySelf: true});

    // load only organizer events or all
    let eventsObservable: Observable<{rows: Event[], count: number}>;
    if (this.eventsBrowseService.organizer) {
      eventsObservable = this.organizersService.getPaginatedEventsByOrganizerUrl(
        this.eventsBrowseService.organizer.url,
      );
      this.newEventsService = this.organizersService;
    } else {
      eventsObservable = this.eventsService.getPaginatedEvents();
      this.newEventsService = this.eventsService;
    }

    // which events page should be loaded
    const searchPage = parseInt(this.activatedRoute.snapshot.queryParamMap.get('page') || '0', 10);
    this.offset = searchPage ? searchPage * this.limit : 0;

    // subscribe to events stream
    this.eventsSubscription = eventsObservable.pipe(
    ).subscribe({
      next: (newEvents) => {
        if (!newEvents || !newEvents.rows || newEvents.rows.length < this.limit ) {
          this.moreToLoad = false;
        } else {
          this.moreToLoad = true;
        }
        this.events.push(
          ...newEvents.rows.filter( (nE) => this.events.reduce( (acc, oE) => acc && nE.url !== oE.url, true)),
        );
        this.isLoadingEvents = false;
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.log('error into getPaginatedEvents function on events-browse component : ', err);
        this.isLoadingEvents = false;
      },
    });

    // load first events
    this.newEventsService.getSomeEvents.next({
      limit : this.limit,
      offset: this.offset,
      searchQuery: this.eventsBrowseService.browseModel.searchQuery,
      sort: this.eventsBrowseService.browseModel.sort,
    });

    // subscribe to search changes
    this.formSubscription = this.eventsBrowseService.browseForm.valueChanges.pipe(
      filter( (v) => !!v.displayStyle && !!v.sort && typeof v.searchQuery === 'string'),
      debounceTime(300),
      skip(1),
      distinctUntilChanged( (prev, curr) => prev.displayStyle === curr.displayStyle &&
        prev.searchQuery === curr.searchQuery &&
        prev.sort === curr.sort,
      ),
    ).subscribe( (search) => {
      this.isLoadingEvents = true;
      this.offset = searchPage ? searchPage * this.limit : 0;
      this.events = [];
      this.newEventsService.getSomeEvents.next({
        limit : this.limit,
        offset: this.offset,
        searchQuery: search.searchQuery,
        sort: search.sort,
      });
    });
  }

  public eventTrackBy(index, event: Event) {
    return event.id ? event.id : event;
  }

  loadMoreEvents() {
    this.isLoadingEvents = true;
    this.offset += this.limit;
    this.newEventsService.getSomeEvents.next({
      limit : this.limit,
      offset: this.offset,
      searchQuery: this.eventsBrowseService.browseModel.searchQuery,
      sort: this.eventsBrowseService.browseModel.sort,
    });
  }

  ngOnDestroy() {
    this.eventsSubscription?.unsubscribe();
    this.formSubscription?.unsubscribe();
  }
}
